import { useIntl } from 'react-intl'
import { mdiArrowLeft } from '@mdi/js'
import { equipmentEntityAdapter } from '@racepointenergy/swc/scs/features/host-equipment'
import { HomeId } from '@racepointenergy/swc/scs/types'
import { RouteComponentProps, useRouteMatch } from 'react-router-dom'
import { dealerMonitorEquipment } from '@racepointenergy/swc/scs/dealer-monitor-equipment'
import { dealerMonitorStates } from '@racepointenergy/swc/scs/dealer-monitor-states'
import { stateEntityAdapter } from '@racepointenergy/swc/scs/features/host-states'
import { useMemo } from 'react'

import {
  BackArrowIcon,
  DeviceContainer,
  BackToAllDevicesLinkContainer,
  BackToAllDevicesTitle,
  DeviceCardFlexboxWrapper,
} from './styles'
import { homeDeviceInformationMessages as messages } from './messages'
import DeviceMainInformation from './components/DeviceMainInformation'

import { Spinner } from 'components/Loader'
import { SpinnerContainer } from '../../HomeStatus/SubInfo/ConnectedApps/styles'
import DeviceInformationCard from './components/DeviceInformationCard'
import { useUiDevices } from '../hooks'

import DeviceWhiteCard from './components/DeviceWhiteCard'
import { DASH_SYMBOL } from 'app/consts'
import DeviceStatus from './DeviceStatus'
import DeviceIPAddress from './DeviceIPAddress'
import DeviceWhiteCardSection from './components/DeviceWhiteCardSection'
import DeviceSignalStrength from './DeviceSignalStrength'

interface HomeDevicesInformationProps
  extends RouteComponentProps<{ homeId: HomeId; id: string }> {}

const equipmentSelectors = equipmentEntityAdapter.getSelectors()
const equipmentInitialState = equipmentEntityAdapter.getInitialState()

const hostStateInitialState = stateEntityAdapter.getInitialState()

function HomeDeviceInformation({
  match: {
    params: { id, homeId },
  },
}: HomeDevicesInformationProps) {
  const { url } = useRouteMatch()
  const { formatMessage } = useIntl()
  const { data = equipmentInitialState, isFetching } =
    dealerMonitorEquipment.useQuery({ id: homeId })

  const { data: stateData = hostStateInitialState, isFetching: stateFetching } =
    dealerMonitorStates.useQuery({ id: homeId })

  const { selectUiDevices } = useUiDevices(homeId)

  const uiDevice = useMemo(() => {
    const uiDeviceId = id in selectUiDevices.entities
    if (uiDeviceId) return selectUiDevices.entities[id]
  }, [id, selectUiDevices.entities])

  const devicesUrl = url.split('/').slice(0, -1).join('/')

  const equipmentUpdate = equipmentSelectors.selectById(data, id)

  const isCellular =
    equipmentUpdate?.UniqueID?.substring(
      equipmentUpdate?.UniqueID?.length - 4
    ) === '0114' ||
    equipmentUpdate?.UniqueID?.substring(
      equipmentUpdate?.UniqueID?.length - 4
    ) === '0116'

  if (isFetching || (!uiDevice && !equipmentUpdate)) {
    return (
      <SpinnerContainer>
        <Spinner />
      </SpinnerContainer>
    )
  }

  const isSavantBranded =
    equipmentUpdate?.DeviceManufacturer === 'Savant' ||
    uiDevice?.DeviceMake?.value === 'Savant'

  return (
    <DeviceContainer>
      <BackToAllDevicesLinkContainer to={devicesUrl}>
        <BackArrowIcon path={mdiArrowLeft} />
        <BackToAllDevicesTitle>
          {formatMessage(messages.backToAllDevices)}
        </BackToAllDevicesTitle>
      </BackToAllDevicesLinkContainer>
      <DeviceMainInformation
        equipmentUpdate={equipmentUpdate}
        uiDevice={uiDevice}
        isSavantBranded={isSavantBranded}
      />
      <DeviceCardFlexboxWrapper>
        <DeviceStatus uiDevice={uiDevice} equipment={equipmentUpdate} />
        {isCellular && (
          <DeviceSignalStrength
            rsrp={
              stateData.entities['global.NetworkConfig.cellular.rsrp']?.value
            }
            rsrq={
              stateData.entities['global.NetworkConfig.cellular.rsrq']?.value
            }
            loading={stateFetching}
            order={2}
          />
        )}
        <DeviceInformationCard
          title={formatMessage(messages.deviceBytesSentLabel)}
          value={equipmentUpdate?.Stats?.BytesSent.toString() || DASH_SYMBOL}
          order={4}
        />
        <DeviceInformationCard
          title={formatMessage(messages.deviceBytesReceivedLabel)}
          value={
            equipmentUpdate?.Stats?.BytesReceived.toString() || DASH_SYMBOL
          }
          order={3}
        />
        <DeviceInformationCard
          title={formatMessage(messages.messagesSentLabel)}
          value={equipmentUpdate?.Stats?.MessagesSent.toString() || DASH_SYMBOL}
          order={5}
        />
        <DeviceInformationCard
          title={formatMessage(messages.messagesReceivedLabel)}
          value={
            equipmentUpdate?.Stats?.MessagesReceived.toString() || DASH_SYMBOL
          }
          order={6}
        />
        <DeviceInformationCard
          title={formatMessage(messages.receiveResponseTimeoutsLabel)}
          value={
            equipmentUpdate?.Stats?.ReceiveTimeouts.toString() || DASH_SYMBOL
          }
          order={7}
        />
        <DeviceInformationCard
          title={formatMessage(messages.deviceUptimeLabel)}
          value={DASH_SYMBOL}
          order={2}
        />
      </DeviceCardFlexboxWrapper>
      <DeviceCardFlexboxWrapper>
        <DeviceWhiteCard order={8}>
          <DeviceWhiteCardSection
            title={formatMessage(messages.deviceFirmwareLabel)}
            value={
              equipmentUpdate?.FirmwareRevision ||
              uiDevice?.FirmwareVersion?.value ||
              uiDevice?.AppVersion?.value ||
              DASH_SYMBOL
            }
          />
          <DeviceWhiteCardSection
            title={formatMessage(messages.deviceFirmwareUpgradeLabel)}
            value={
              equipmentUpdate?.FWUpdateStage ||
              uiDevice?.UpdateProgress?.value ||
              (uiDevice?.UpdateState?.value === 'idle'
                ? 'Up to date'
                : uiDevice?.UpdateState?.value) ||
              DASH_SYMBOL
            }
          />
        </DeviceWhiteCard>
        <DeviceWhiteCard order={9}>
          {isSavantBranded && (
            <DeviceWhiteCardSection
              title={formatMessage(messages.deviceBootRomVersionLabel)}
              value={equipmentUpdate?.BootRomRevision || DASH_SYMBOL}
            />
          )}
          <DeviceWhiteCardSection
            title={formatMessage(messages.deviceSoftwareVersion)}
            value={uiDevice?.SoftwareVersion?.value || DASH_SYMBOL}
          />
          <DeviceIPAddress uiDevice={uiDevice} equipment={equipmentUpdate} />
        </DeviceWhiteCard>
        <DeviceWhiteCard order={10}>
          <DeviceWhiteCardSection
            title={formatMessage(messages.deviceSerialNumberLabel)}
            value={
              equipmentUpdate?.SerialNumber ||
              uiDevice?.SerialNumber?.value ||
              DASH_SYMBOL
            }
          />
          <DeviceWhiteCardSection
            title={formatMessage(messages.devicePartNumberLabel)}
            value={
              equipmentUpdate?.PartNumber ||
              uiDevice?.PartNumber?.value ||
              DASH_SYMBOL
            }
          />
        </DeviceWhiteCard>
        <DeviceWhiteCard order={11}>
          <DeviceWhiteCardSection
            title={formatMessage(messages.deviceWifi)}
            value={uiDevice?.CurrentNetwork?.value || DASH_SYMBOL}
          />
          <DeviceWhiteCardSection
            title={formatMessage(messages.deviceNetworkLevel)}
            value={uiDevice?.WifiLevel?.value || DASH_SYMBOL}
          />
        </DeviceWhiteCard>
        <DeviceWhiteCard order={12}>
          <DeviceWhiteCardSection
            title={formatMessage(messages.profileLabel)}
            value={equipmentUpdate?.Stats?.ProfileInfo || DASH_SYMBOL}
          />
        </DeviceWhiteCard>
      </DeviceCardFlexboxWrapper>
    </DeviceContainer>
  )
}

export default HomeDeviceInformation
